import React, { useState } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Button,
  VStack,
  HStack,
  useToast,
  Flex,
  Icon,
  useColorModeValue
} from '@chakra-ui/react';
import { FaEnvelope, FaMapMarkerAlt, FaPhone } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { texts } from '../lang';
import { useLanguage } from '../context/LanguageContext';
import { COLORS, CONTACT_INFO } from '../globalvariables';
import emailjs from '@emailjs/browser';


const MotionBox = motion(Box);

const Contact = () => {
  const { language } = useLanguage();
  const contactTexts = texts[language].contact;
  const toast = useToast();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
  
    try {
      // ✅ Juste ici tu mets l'appel réel à EmailJS :
      await emailjs.send(
        'service_ciu0e4q',           // Service ID
        'template_yj4ahqw',          // Template ID
        {
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          message: formData.message,
        },
        'bf1_RH_rGByW1qBE4'          // Public Key
      );
  
      toast({
        title: contactTexts.form.successMessage,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
  
      // Réinitialise le formulaire
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
  
    } catch (error) {
      console.error('Erreur EmailJS :', error);
      toast({
        title: contactTexts.form.errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const inputBg = COLORS.cardBackground;
  const inputColor = COLORS.textPrimary;
  
  return (
    <Box bg={COLORS.background} color={COLORS.textPrimary} minH="100vh" pt={{ base: 16, md: 20 }} pb={{ base: 12, md: 16 }}>
      <Container maxW="1200px" px={{ base: 2, md: 6 }}>
        <MotionBox
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          mb={{ base: 8, md: 12 }}
          textAlign="center"
        >
          <Heading as="h1" size={{ base: "xl", md: "2xl" }} mb={{ base: 3, md: 4 }}>{contactTexts.title}</Heading>
          <Text fontSize={{ base: "lg", md: "xl" }}>{contactTexts.description}</Text>
        </MotionBox>
        
        <Flex 
          direction={{ base: 'column', lg: 'row' }} 
          gap={{ base: 6, md: 10 }}
          align="start"
          w="100%"
        >
          {/* Formulaire de contact */}
          <MotionBox
            flex="1"
            w={{ base: "100%", lg: "auto" }}
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Box
              as="form"
              onSubmit={handleSubmit}
              bg={COLORS.cardBackground}
              p={{ base: 3, md: 8 }}
              borderRadius="xl"
              boxShadow="lg"
              borderWidth="1px"
              borderColor="whiteAlpha.200"
              w="100%"
            >
              <VStack spacing={{ base: 4, md: 5 }}>
                <FormControl isRequired>
                  <FormLabel fontSize={{ base: "sm", md: "md" }}>{contactTexts.form.nameLabel}</FormLabel>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder={contactTexts.form.namePlaceholder}
                    bg={inputBg}
                    color={inputColor}
                    borderColor="rgba(0,0,0,0.2)"
                    _hover={{ borderColor: COLORS.primary }}
                    _focus={{ borderColor: COLORS.primary, boxShadow: `0 0 0 1px ${COLORS.primary}` }}
                    size={{ base: "md", md: "lg" }}
                  />
                </FormControl>
                
                <FormControl isRequired>
                  <FormLabel fontSize={{ base: "sm", md: "md" }}>{contactTexts.form.emailLabel}</FormLabel>
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={contactTexts.form.emailPlaceholder}
                    bg={inputBg}
                    color={inputColor}
                    borderColor="rgba(0,0,0,0.2)"
                    _hover={{ borderColor: COLORS.primary }}
                    _focus={{ borderColor: COLORS.primary, boxShadow: `0 0 0 1px ${COLORS.primary}` }}
                    size={{ base: "md", md: "lg" }}
                  />
                </FormControl>
                
                <FormControl isRequired>
                  <FormLabel fontSize={{ base: "sm", md: "md" }}>{contactTexts.form.subjectLabel}</FormLabel>
                  <Input
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder={contactTexts.form.subjectPlaceholder}
                    bg={inputBg}
                    color={inputColor}
                    borderColor="rgba(0,0,0,0.2)"
                    _hover={{ borderColor: COLORS.primary }}
                    _focus={{ borderColor: COLORS.primary, boxShadow: `0 0 0 1px ${COLORS.primary}` }}
                    size={{ base: "md", md: "lg" }}
                  />
                </FormControl>
                
                <FormControl isRequired>
                  <FormLabel fontSize={{ base: "sm", md: "md" }}>{contactTexts.form.messageLabel}</FormLabel>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder={contactTexts.form.messagePlaceholder}
                    bg={inputBg}
                    color={inputColor}
                    borderColor="rgba(0,0,0,0.2)"
                    _hover={{ borderColor: COLORS.primary }}
                    _focus={{ borderColor: COLORS.primary, boxShadow: `0 0 0 1px ${COLORS.primary}` }}
                    rows={{ base: 4, md: 6 }}
                    resize="vertical"
                  />
                </FormControl>
                
                <Button
                  type="submit"
                  colorScheme="green"
                  bg={COLORS.primary}
                  color={COLORS.background}
                  size={{ base: "md", md: "lg" }}
                  width="full"
                  fontWeight="bold"
                  _hover={{ bg: COLORS.primaryHover }}
                  isLoading={isSubmitting}
                  loadingText="Envoi..."
                >
                  {contactTexts.form.submitButton}
                </Button>
              </VStack>
            </Box>
          </MotionBox>
          
          {/* Coordonnées et carte */}
          <MotionBox
            flex="1"
            w={{ base: "100%", lg: "auto" }}
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <Box
              bg={COLORS.cardBackground}
              p={{ base: 4, md: 8 }}
              borderRadius="xl"
              boxShadow="lg"
              mb={{ base: 4, md: 8 }}
              borderWidth="1px"
              borderColor="whiteAlpha.200"
            >
              <Heading as="h3" size={{ base: "md", md: "lg" }} mb={{ base: 4, md: 6 }}>{contactTexts.title}</Heading>
              
              <VStack spacing={{ base: 4, md: 6 }} align="start">
                <HStack spacing={{ base: 3, md: 4 }}>
                  <Flex
                    bg={COLORS.primary}
                    w={{ base: "40px", md: "50px" }}
                    h={{ base: "40px", md: "50px" }}
                    borderRadius="full"
                    align="center"
                    justify="center"
                    color={COLORS.background}
                  >
                    <Icon as={FaMapMarkerAlt} w={{ base: 5, md: 6 }} h={{ base: 5, md: 6 }} />
                  </Flex>
                  <Box>
                    <Text fontWeight="bold" fontSize={{ base: "md", md: "lg" }}>{contactTexts.address}</Text>
                    <Text fontSize={{ base: "sm", md: "md" }}>{CONTACT_INFO.address}</Text>
                  </Box>
                </HStack>
                
                <HStack spacing={{ base: 3, md: 4 }}>
                  <Flex
                    bg={COLORS.primary}
                    w={{ base: "40px", md: "50px" }}
                    h={{ base: "40px", md: "50px" }}
                    borderRadius="full"
                    align="center"
                    justify="center"
                    color={COLORS.background}
                  >
                    <Icon as={FaPhone} w={{ base: 5, md: 6 }} h={{ base: 5, md: 6 }} />
                  </Flex>
                  <Box>
                    <Text fontWeight="bold" fontSize={{ base: "md", md: "lg" }}>{contactTexts.phone}</Text>
                    <Text fontSize={{ base: "sm", md: "md" }}>{CONTACT_INFO.phone}</Text>
                  </Box>
                </HStack>
                
                <HStack spacing={{ base: 3, md: 4 }}>
                  <Flex
                    bg={COLORS.primary}
                    w={{ base: "40px", md: "50px" }}
                    h={{ base: "40px", md: "50px" }}
                    borderRadius="full"
                    align="center"
                    justify="center"
                    color={COLORS.background}
                  >
                    <Icon as={FaEnvelope} w={{ base: 5, md: 6 }} h={{ base: 5, md: 6 }} />
                  </Flex>
                  <Box>
                    <Text fontWeight="bold" fontSize={{ base: "md", md: "lg" }}>{contactTexts.email}</Text>
                    <Text fontSize={{ base: "sm", md: "md" }}>{CONTACT_INFO.email}</Text>
                  </Box>
                </HStack>
              </VStack>
            </Box>
            
            {/* Carte (simulation) */}
            <Box
              h={{ base: "200px", md: "300px" }}
              bg="gray.200"
              borderRadius="xl"
              overflow="hidden"
              position="relative"
              border="1px solid"
              borderColor="gray.300"
            >
              <Box
                as="iframe"
                src={`https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2625.4763130654354!2d2.330037376942624!3d48.85245067133293!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47e671d9f7fae695%3A0xfa2c99aef11bf424!2s${encodeURIComponent(CONTACT_INFO.address)}!5e0!3m2!1s${language}!2sfr!4v1697047948144!5m2!1s${language}!2sfr`}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </Box>
          </MotionBox>
        </Flex>
      </Container>
    </Box>
  );
};

export default Contact;